import {
  Badge,
  Box,
  Button,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tag,
  Text,
} from '@chakra-ui/react';
import { formatInTimeZone } from 'date-fns-tz';
import { ImageWithSkeleton } from 'features/common/components/ImageWithSkeleton';
import { history } from 'features/common/history/history';
import { CONTEXT_IDS_TEXT } from 'features/context-ids/constants';
import {
  getContextIdsDeleteRoute,
  getContextIdsDetailsRoute,
} from 'features/context-ids/routes';
import {
  ExtendedSitecoreContextType,
  SitecoreContextMappingType,
  SitecoreContextResourceType,
  SitecoreContextType,
} from 'features/context-ids/types/types';
import {
  getResourceDisplayConfig,
  isDisplayableResource,
} from 'features/context-ids/utils';
import { Row } from 'react-table';

type CellProps<T = string> = {
  row: Row<ExtendedSitecoreContextType | SitecoreContextMappingType>;
  value: T;
};

export const getEdgeVariantFilterKey = (
  resource: Pick<SitecoreContextResourceType, 'type' | 'tenantProductCode'>,
): 'edge-live' | 'edge-preview' | null => {
  if (resource.type?.toLowerCase() !== 'edge') {
    return null;
  }

  return resource.tenantProductCode?.toLowerCase() === 'edge'
    ? 'edge-live'
    : 'edge-preview';
};

// Helper function to check if a resource matches the selected filter value
export const matchesResourceFilter = (
  resource: SitecoreContextResourceType,
  filterValue: string,
): boolean => {
  const lowerFilterValue = filterValue.toLowerCase();
  const tenantProductCode = resource.tenantProductCode?.toLowerCase() || '';
  const edgeVariantFilterKey = getEdgeVariantFilterKey(resource);

  // Handle special Edge cases
  if (lowerFilterValue === 'edge-live') {
    return edgeVariantFilterKey === 'edge-live';
  }

  if (lowerFilterValue === 'edge-preview') {
    return edgeVariantFilterKey === 'edge-preview';
  }

  // The "search" filter covers:
  //  • resources with tenantProductCode "search" (standard)
  //  • legacy "discover" resources
  //  • user-created Search resource whose tenantProductCode is "SearchEmbedded"
  //    (EP API quirk — identified by its well-known ID via getResourceDisplayConfig)
  if (lowerFilterValue === 'search') {
    return (
      tenantProductCode === 'search' ||
      tenantProductCode === 'discover' ||
      (resource.type?.toLowerCase() === 'search' &&
        !!getResourceDisplayConfig({
          tenantProductCode: resource.tenantProductCode,
          type: resource.type,
          id: resource.id,
        }))
    );
  }

  return tenantProductCode === lowerFilterValue;
};

// Helper to check if a context matches search (including scoped contexts)
export const matchesSearch = (
  context: ExtendedSitecoreContextType,
  searchTerm: string,
): boolean => {
  const lowerSearchTerm = searchTerm.toLowerCase();
  return !!(
    context.sitecoreContextId?.toLowerCase().includes(lowerSearchTerm) ||
    context.name?.toLowerCase().includes(lowerSearchTerm) ||
    (context.description &&
      context.description.toLowerCase().includes(lowerSearchTerm))
  );
};

// Helper to check if a context matches resource filter
export const matchesResourceFilterForContext = (
  context: ExtendedSitecoreContextType,
  selectedResourceValues: string[],
): boolean => {
  return (
    context.resources
      ?.filter((resource) => isDisplayableResource(resource))
      ?.some((resource) =>
        selectedResourceValues.some((filterValue) =>
          matchesResourceFilter(resource, filterValue),
        ),
      ) ?? false
  );
};

export const getTableOptions = (
  handleRegenerate: (sitecoreContextId: string) => Promise<void>,
  handleRetryScopedContext?: (contextId: string) => void,
) => ({
  rowActions: ({
    original,
  }: {
    original: SitecoreContextMappingType | ExtendedSitecoreContextType;
  }) => {
    // Hide row actions for scoped contexts, error rows, and skeleton rows
    const extendedOriginal = original as ExtendedSitecoreContextType;
    if (
      extendedOriginal.isScopedContext ||
      extendedOriginal.isError ||
      extendedOriginal.isLoading
    ) {
      return [];
    }

    const { sitecoreContextId, type: sitecoreContextType } = original;
    return [
      {
        id: '0',
        label: CONTEXT_IDS_TEXT.VIEW_DETAILS_ACTION,
        onClick: () => {
          history.push({
            pathname: getContextIdsDetailsRoute(sitecoreContextId),
            search: history.location.search,
          });
        },
        'data-behavior-analytics-id': 'View details - context id',
      },
      ...(sitecoreContextType === 'custom'
        ? [
            {
              id: '1',
              label: CONTEXT_IDS_TEXT.DELETE_ACTION,
              onClick: () => {
                history.push({
                  pathname: getContextIdsDeleteRoute(sitecoreContextId),
                  search: history.location.search,
                });
              },
              'data-behavior-analytics-id': 'View details - delete context id',
            },
          ]
        : [
            {
              id: '2',
              label: CONTEXT_IDS_TEXT.REGENERATE_ACTION,
              onClick: () => handleRegenerate(sitecoreContextId),
              'data-behavior-analytics-id': 'regenerate-context-id-action',
            },
          ]),
    ];
  },
  columns: [
    {
      id: 'sitecoreContextId',
      Header: CONTEXT_IDS_TEXT.CONTEXT_ID_COLUMN,
      accessor: (row: ExtendedSitecoreContextType) => {
        // Don't sort by scoped contexts - return empty string so they don't affect sorting
        if (row.isScopedContext) {
          return '';
        }
        return row.sitecoreContextId;
      },
      Cell: ({
        row,
      }: {
        row: Row<ExtendedSitecoreContextType | SitecoreContextMappingType>;
      }) => {
        const original = row.original as ExtendedSitecoreContextType;

        if (original.isError) {
          return (
            <Flex alignItems='center' gap={2} height='40px'>
              <Text color='red.500'>Failed to load scoped contexts.</Text>
              {handleRetryScopedContext &&
                'contextId' in original &&
                typeof (original as { contextId?: string }).contextId ===
                  'string' && (
                  <Button
                    variant='link'
                    size='sm'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryScopedContext(
                        (original as { contextId: string }).contextId,
                      );
                    }}
                  >
                    Retry
                  </Button>
                )}
            </Flex>
          );
        }

        if (original.isLoading) {
          return <Skeleton w='full' height='8' />;
        }

        return renderContextIdCell(row.original);
      },
      disableSortBy: false,
    },
    {
      id: 'name',
      Header: CONTEXT_IDS_TEXT.NAME_COLUMN,
      accessor: (row: ExtendedSitecoreContextType) => {
        // Don't sort by scoped contexts - return empty string so they don't affect sorting
        if (row.isScopedContext) {
          return '';
        }
        return row.name;
      },
      Cell: ({ row, value }: CellProps<string>) => {
        const original = row.original as ExtendedSitecoreContextType;
        if (original.isError) {
          return null; // Error message spans Context ID column
        }

        if (original.isLoading) {
          return <Skeleton w='full' height='8' />;
        }

        // For scoped contexts, accessor returns empty string, so use original.name
        const displayName = original.isScopedContext ? original.name : value;
        return (
          <Text isTruncated title={displayName} maxW='xs'>
            {displayName}
          </Text>
        );
      },
      disableSortBy: false,
    },
    {
      id: 'description',
      Header: CONTEXT_IDS_TEXT.DESCRIPTION_COLUMN,
      accessor: (row: ExtendedSitecoreContextType) => {
        // Don't sort by scoped contexts - return empty string so they don't affect sorting
        if (row.isScopedContext) {
          return '';
        }
        return row.description;
      },
      Cell: ({ row, value }: CellProps<string>) => {
        const original = row.original as ExtendedSitecoreContextType;
        if (original.isError) {
          return null;
        }

        if (original.isLoading) {
          return <Skeleton w='full' height='8' />;
        }

        return (
          <Text isTruncated title={value} maxW='md'>
            {value}
          </Text>
        );
      },
      disableSortBy: false,
    },
    {
      id: 'updatedAt',
      Header: CONTEXT_IDS_TEXT.UPDATED_AT_COLUMN,
      accessor: (row: ExtendedSitecoreContextType) => {
        // Don't sort by scoped contexts - return null so they don't affect sorting
        if (row.isScopedContext) {
          return null;
        }
        return row.updatedAt;
      },
      Cell: ({ row, value }: CellProps<string | null | undefined>) => {
        const original = row.original as ExtendedSitecoreContextType;
        if (original.isError) {
          return null;
        }

        if (original.isLoading) {
          return <Skeleton w='full' height='8' />;
        }

        // For scoped contexts, accessor returns null, so use original.updatedAt
        const dateValue = original.isScopedContext ? original.updatedAt : value;
        if (!dateValue) {
          return null;
        }

        const dateOnly = formatInTimeZone(
          new Date(dateValue).getTime(),
          'UTC',
          'MMM d, yyyy',
        );
        const timeOnly = formatInTimeZone(
          new Date(dateValue).getTime(),
          'UTC',
          'h:mm a',
        );

        return (
          <>
            <span style={{ whiteSpace: 'nowrap' }}>{dateOnly}</span>
            <br />
            <span style={{ whiteSpace: 'nowrap' }}>{timeOnly} UTC</span>
          </>
        );
      },
      sortType: (
        rowA: Row<ExtendedSitecoreContextType>,
        rowB: Row<ExtendedSitecoreContextType>,
      ) => {
        // Don't sort scoped contexts - they should always stay with their parent
        const originalA = rowA.original as ExtendedSitecoreContextType;
        const originalB = rowB.original as ExtendedSitecoreContextType;
        if (originalA.isScopedContext || originalB.isScopedContext) {
          return 0;
        }
        const a = new Date(originalA.updatedAt || 0).getTime();
        const b = new Date(originalB.updatedAt || 0).getTime();
        return a - b;
      },
      disableSortBy: false,
    },
    {
      id: 'resources',
      Header: CONTEXT_IDS_TEXT.RESOURCES_HEADER,
      accessor: (row: ExtendedSitecoreContextType) => {
        // Don't sort by scoped contexts - return empty string so they don't affect sorting
        if (row.isScopedContext) {
          return '';
        }

        // Sort based on first resource in the list by name
        const filteredResources = row.resources?.filter((r) =>
          isDisplayableResource(r),
        );
        return filteredResources?.[0]?.name?.toLowerCase() || '';
      },
      Cell: ({
        row,
      }: {
        row: Row<ExtendedSitecoreContextType | SitecoreContextMappingType>;
      }) => {
        const original = row.original as ExtendedSitecoreContextType;
        if (original.isError) {
          return null; // Error message spans Context ID column
        }

        if (original.isLoading) {
          return <Skeleton w='full' height='8' />;
        }

        return renderResourcesCell(row.original);
      },
      disableSortBy: false,
    },
  ],
});

export const renderResourcesCell = ({
  resources,
}: Partial<SitecoreContextType>) => {
  const filteredResources = resources?.filter((r) => isDisplayableResource(r));
  const hasFilteredResources = filteredResources?.length ?? 0;
  const hasMoreThanFourResources = hasFilteredResources > 4;
  const displayedResources = filteredResources?.slice(0, 4);
  const remainingResources = filteredResources?.slice(4);

  const renderIcon = (resource: SitecoreContextResourceType, index: number) => {
    const { iconUrl, name: displayName } =
      getResourceDisplayConfig(resource) ?? {};
    return (
      <ImageWithSkeleton
        key={index}
        width='7'
        height='7'
        src={iconUrl}
        alt={displayName}
        flexShrink={0}
      />
    );
  };

  return (
    <Flex gap={1} minW='max-content'>
      {displayedResources?.length ? (
        <>
          {displayedResources.map(renderIcon)}
          {hasMoreThanFourResources && (
            <Popover trigger='hover'>
              <PopoverTrigger>
                <Box
                  as='button'
                  type='button'
                  width='7'
                  height='7'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  bg='gray.100'
                  border='none'
                  borderRadius='md'
                  flexShrink={0}
                  cursor='pointer'
                  aria-label={`Show ${remainingResources?.length} more resources`}
                >
                  <Text fontSize='sm'>+{remainingResources?.length}</Text>
                </Box>
              </PopoverTrigger>

              <PopoverContent width='auto'>
                <PopoverBody>
                  <Flex gap={1}>{remainingResources?.map(renderIcon)}</Flex>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </>
      ) : (
        <Text variant='subtle'>{CONTEXT_IDS_TEXT.NO_RESOURCES_ADDED}</Text>
      )}
    </Flex>
  );
};

export const renderContextIdCell = ({
  sitecoreContextId,
  type,
  resources,
  isScopedContext,
}: Partial<SitecoreContextType & { isScopedContext?: boolean }>) => {
  // The current assumption is that there will only be one Edge resource per context.
  // However, we have chosen to go with the approach below to be future-proof or if there are edge cases.
  const edgeResources =
    resources?.filter((resource) => resource.type === 'edge') || [];
  const hasLiveEdge = edgeResources.some(
    (resource) => resource.tenantProductCode === 'Edge',
  );
  const hasPreviewEdge = edgeResources.some(
    (resource) =>
      resource.tenantProductCode !== 'Edge' && resource.type === 'edge',
  );

  return (
    <Flex alignItems='center' gap={2}>
      <Text>{sitecoreContextId}</Text>
      {!isScopedContext && type !== 'custom' && (
        <Tag px={2} py={1} fontSize='xs' fontWeight='semibold'>
          {CONTEXT_IDS_TEXT.MASTER_TAG}
        </Tag>
      )}
      {hasLiveEdge && (
        <Badge textTransform='capitalize' colorScheme='success'>
          Live
        </Badge>
      )}
      {hasPreviewEdge && (
        <Badge textTransform='capitalize' colorScheme='neutral'>
          Preview
        </Badge>
      )}
    </Flex>
  );
};
